import {
  AIMessage,
  ToolCallData,
  ToolMessage,
  UserMessage,
} from "@/features/Prompt/schema";
import {
  TextMessagePart as TextContentPart,
  ThreadAssistantMessagePart as ThreadAssistantContentPart,
  ThreadAssistantMessage,
  ThreadMessage,
  ThreadUserMessagePart as ThreadUserContentPart,
  ThreadUserMessage,
} from "@assistant-ui/react";
import { FrontPosition } from "../../event";

/**
 * Type for custom metadata in messages
 */
export type MessageCustomMetadata = {
  backend_id: number;
  parent_id: number | null;
  [key: string]: any;
};

/**
 * Type representing a message with its parent ID
 */
export type MessageWithParent<T extends ThreadMessage> = {
  message: T;
  parentId: string | null;
};

/**
 * Type for tool call processing result
 */
export type ToolCallProcessingResult = {
  toolCallPart: ThreadAssistantContentPart;
  position: FrontPosition;
};

/**
 * Type for a message tree node with its children
 */
export type MessageTreeNode = {
  msg: AIMessage;
  children: number[];
  processed: boolean;
};

/**
 * Type for a message tree (mapping of message IDs to nodes)
 */
export type MessageTree = Record<number, MessageTreeNode>;

/**
 * Utility class for general date operations
 */
export class DateUtils {
  /**
   * Creates a fallback date when created_at is missing
   */
  static _createFallbackDate(): Date {
    return new Date();
  }

  /**
   * Safely parses a date string with fallback
   * @param dateString - The date string to parse, or undefined
   */
  static safelyParseDate(dateString?: string): Date {
    if (!dateString) {
      return DateUtils._createFallbackDate();
    }

    try {
      return new Date(dateString);
    } catch (e) {
      console.warn("Error parsing date:", dateString);
      return DateUtils._createFallbackDate();
    }
  }
}

/**
 * Utility class for ID generation and manipulation
 */
export class IdUtils {
  /**
   * Creates a unique message ID from backend ID
   * @param messageId - The numeric backend message ID
   */
  static createMessageId(messageId: number): string {
    return `msg-${messageId}`;
  }

  /**
   * Creates a parent ID from raw parent ID
   * @param rawParentId - The raw parent ID from backend
   */
  static createParentId(rawParentId: number | null): string | null {
    return rawParentId ? IdUtils.createMessageId(rawParentId) : null;
  }
}

/**
 * Utility class for content parts creation and manipulation
 */
export class ContentUtils {
  /**
   * Creates text content part from string
   * @param text - The text content
   */
  static createTextContent(text: string): TextContentPart {
    return { type: "text", text };
  }

  /**
   * Creates a user content array from message content
   */
  static createUserContentParts(content: string): ThreadUserContentPart[] {
    return [ContentUtils.createTextContent(content)];
  }

  /**
   * Adds text content to result parts if not empty
   * @param content - The text content to add
   * @param resultContentParts - The array to add the content to
   */
  static addTextContentIfNotEmpty(
    content: string | undefined,
    resultContentParts: ThreadAssistantContentPart[],
  ): void {
    if (content && content.trim() !== "") {
      resultContentParts.push(ContentUtils.createTextContent(content));
    }
  }

  /**
   * Combines all content parts into final array
   * @param atStartTools - Array of tools at start position
   * @param resultContentParts - Array of content parts
   * @param atEndTools - Array of tools at end position
   */
  static combineContentParts(
    atStartTools: ThreadAssistantContentPart[],
    resultContentParts: ThreadAssistantContentPart[],
    atEndTools: ThreadAssistantContentPart[],
  ): ThreadAssistantContentPart[] {
    return [...atStartTools, ...resultContentParts, ...atEndTools];
  }
}

/**
 * Utility class for message-related operations
 */
export class MessageUtils {
  /**
   * Creates a user message object
   */
  static createUserMessage({
    id,
    content,
    createdAt,
    metadata,
  }: {
    id: string;
    content: ThreadUserContentPart[];
    createdAt: Date;
    metadata: any;
  }): ThreadUserMessage {
    return {
      id,
      role: "user",
      content,
      createdAt,
      attachments: [],
      metadata,
    };
  }

  /**
   * Creates an assistant message object
   * @param id - The ID for the new message
   * @param content - The content of the message
   * @param createdAt - The creation date
   * @param metadata - The message metadata
   */
  static createAssistantMessage({
    id,
    content,
    createdAt,
    metadata,
  }: {
    id: string;
    content: ThreadAssistantContentPart[];
    createdAt: Date;
    metadata: any;
  }): ThreadAssistantMessage {
    return {
      id,
      role: "assistant",
      content,
      createdAt,
      status: { type: "complete", reason: "stop" },
      metadata,
    };
  }

  /**
   * Creates complete assistant metadata including annotations
   */
  static createAssistantMetadata({
    backendId,
    parentId,
    additionalCustomData,
  }: {
    backendId: number;
    parentId: number | null;
    additionalCustomData?: Record<string, any>;
  }) {
    return {
      unstable_annotations: [],
      unstable_data: [],
      steps: [],
      custom: {
        backend_id: backendId,
        parent_id: parentId,
        ...additionalCustomData,
      },
    };
  }

  /**
   * Converts a user message to ThreadUserMessage format
   */
  static convertUserMessage({
    msg,
    id,
    rawParentId,
  }: {
    msg: UserMessage;
    id: string;
    rawParentId: number | null;
  }): MessageWithParent<ThreadUserMessage> {
    const userContent = ContentUtils.createUserContentParts(msg.content);

    const metadata = {
      custom: {
        backend_id: msg.id,
        parent_id: rawParentId,
      },
    };

    const userMessage = MessageUtils.createUserMessage({
      id,
      content: userContent,
      createdAt: DateUtils.safelyParseDate(msg.created_at),
      metadata,
    });

    return {
      message: userMessage,
      parentId: IdUtils.createParentId(rawParentId),
    };
  }

  /**
   * Creates a default message for unknown message types
   */
  static createDefaultMessage({
    id,
    msgId,
    rawParentId,
    createdAt,
  }: {
    id: string;
    msgId: number;
    rawParentId: number | null;
    createdAt?: string;
  }): MessageWithParent<ThreadAssistantMessage> {
    const metadata = MessageUtils.createAssistantMetadata({
      backendId: msgId,
      parentId: rawParentId,
      additionalCustomData: {
        is_unknown_type: true,
      },
    });

    const defaultMessage = MessageUtils.createAssistantMessage({
      id,
      content: [ContentUtils.createTextContent("Unknown message type")],
      createdAt: DateUtils.safelyParseDate(createdAt),
      metadata,
    });

    return {
      message: defaultMessage,
      parentId: IdUtils.createParentId(rawParentId),
    };
  }

  /**
   * Groups and converts consecutive assistant and tool messages into a single ThreadAssistantMessage
   */
  static convertGroupedMessages(
    messages: AIMessage[],
    startId: string,
    rawParentId: number | null,
  ): MessageWithParent<ThreadAssistantMessage> {
    const resultContentParts: ThreadAssistantContentPart[] = [];
    const atStartTools: ThreadAssistantContentPart[] = [];
    const atEndTools: ThreadAssistantContentPart[] = [];

    // Process each message in the group
    messages.forEach((msg) => {
      MessageUtils._processMessageInGroup(
        msg,
        resultContentParts,
        atStartTools,
        atEndTools,
      );
    });

    // Mark first tool in each position group
    if (atStartTools.length > 0) {
      ToolUtils._markFirstToolInList(atStartTools);
    }

    if (atEndTools.length > 0) {
      ToolUtils._markFirstToolInList(atEndTools);
    }

    // Combine all content parts
    const finalContentParts = ContentUtils.combineContentParts(
      atStartTools,
      resultContentParts,
      atEndTools,
    );

    // Get data for the combined message
    const firstMessage = messages[0];
    const lastBackendIdMessage = messages[messages.length - 1].id;

    // Create metadata for the combined message
    const metadata = MessageUtils.createAssistantMetadata({
      backendId: lastBackendIdMessage,
      parentId: rawParentId,
      additionalCustomData: { is_grouped_message: true },
    });

    // Create the combined message
    const combinedMessage = MessageUtils.createAssistantMessage({
      id: startId,
      content: finalContentParts,
      createdAt: DateUtils.safelyParseDate(firstMessage.created_at),
      metadata,
    });

    return {
      message: combinedMessage,
      parentId: IdUtils.createParentId(rawParentId),
    };
  }

  /**
   * Processes a single message in a group and extracts its content
   */
  static _processMessageInGroup(
    msg: AIMessage,
    resultContentParts: ThreadAssistantContentPart[],
    atStartTools: ThreadAssistantContentPart[],
    atEndTools: ThreadAssistantContentPart[],
  ): void {
    if (msg.role === "tool") {
      const toolMsg = msg as ToolMessage;
      ToolUtils._processToolsFromMessage(
        toolMsg,
        atStartTools,
        resultContentParts,
        atEndTools,
      );
    } else if (msg.role === "assistant") {
      // Type assertion to include tool_calls field
      const assistantMsg = msg as AIMessage & {
        tool_calls?: Record<string, ToolCallData> | null;
      };

      // Check if this assistant message has text transformation tool calls
      if (
        assistantMsg.tool_calls &&
        Object.keys(assistantMsg.tool_calls).length > 0
      ) {
        // Process text with embedded tool calls
        const processedContent = ToolUtils._processTextTransformToolCalls(
          assistantMsg.content,
          assistantMsg.tool_calls,
        );
        resultContentParts.push(...processedContent);
      } else {
        // Regular text content
        ContentUtils.addTextContentIfNotEmpty(
          assistantMsg.content,
          resultContentParts,
        );
      }
    }
  }

  /**
   * Check if a message is an assistant or tool message
   */
  static isAssistantOrToolMessage(msg: AIMessage): boolean {
    return msg.role === "assistant" || msg.role === "tool";
  }
}

/**
 * Utility class for tool-related operations
 */
export class ToolUtils {
  /**
   * Determines if a tool position is at start
   * @param position - The tool position to check
   */
  static _isToolAtStart(position: FrontPosition): boolean {
    return position === "at_start";
  }

  /**
   * Determines if a tool executed successfully
   * @param toolResult - The tool result status string
   */
  static _isToolSuccessful(toolResult?: string): boolean {
    return toolResult === "tool_success";
  }

  /**
   * Determines if a tool position is in conversation
   * @param position - The tool position to check
   */
  static _isToolInConversation(position: FrontPosition): boolean {
    return (
      position === "in_conversation_tool_start" ||
      position === "in_conversation_tool_end"
    );
  }

  /**
   * Checks if a message has valid tool calls
   * @param message - The message to check
   */
  static _hasValidToolCalls(message: ToolMessage): boolean {
    return Boolean(
      message.tool_calls &&
      typeof message.tool_calls === "object" &&
      Object.keys(message.tool_calls).length > 0,
    );
  }

  /**
   * Updates the first tool in a list to mark it as first
   * @param tools - List of tools to update
   */
  static _markFirstToolInList(tools: ThreadAssistantContentPart[]): void {
    if (tools.length === 0) {
      return;
    }

    const firstTool = tools[0];
    if (firstTool.type === "tool-call" && "result" in firstTool) {
      const result = firstTool.result as any;
      if (result) {
        result.isFirstTool = true;
      }
    }
  }

  /**
   * Extracts the component data from a tool call
   * @param toolCall - The tool call data
   */
  static _extractComponentData(toolCall: ToolCallData): any {
    return toolCall.front_component_data || {};
  }

  /**
   * Extracts the position from a tool call
   * @param toolCall - The tool call data
   */
  static _extractPosition(toolCall: ToolCallData): FrontPosition {
    return (toolCall.front_position as FrontPosition) || "at_end";
  }

  /**
   * Creates tool data for a tool call
   * @param backendToolName - Name of the tool from backend
   * @param isFirstTool - Whether this is the first tool in its category
   * @param data - Tool arguments as parsed JSON
   */
  static _createToolData(
    backendToolName: string,
    isFirstTool: boolean,
    data: any,
  ) {
    return {
      backendToolName,
      isFirstTool,
      data,
    };
  }

  /**
   * Parses tool arguments from a string
   * @param argsText - The arguments as a string
   */
  static _parseToolArguments(
    argsText: string | null | undefined,
  ): Record<string, any> {
    if (!argsText || !argsText.trim()) {
      return {};
    }

    try {
      return JSON.parse(argsText);
    } catch (e) {
      console.error("Failed to parse tool arguments:", e);
      return {};
    }
  }

  /**
   * Creates a tool result object
   * @param toolResult - The tool result status string
   * @param toolData - The data returned by the tool
   * @param errorMessage - Optional error message if the tool failed
   */
  static _createToolResult(
    toolResult: string | undefined,
    toolData: any,
    errorMessage?: string,
  ) {
    if (ToolUtils._isToolSuccessful(toolResult)) {
      return toolData;
    }

    return {
      error: errorMessage,
      data: toolData.data,
    };
  }

  /**
   * Creates a tool call part from tool data
   */
  static _createToolCallPart({
    toolId,
    toolName,
    argsText,
    toolResult,
    toolData,
    errorMessage,
  }: {
    toolId: string;
    toolName?: string | null;
    argsText?: string;
    toolResult?: string;
    toolData: any;
    errorMessage?: string;
  }): ThreadAssistantContentPart {
    const parsedArgs = ToolUtils._parseToolArguments(argsText);
    const result = ToolUtils._createToolResult(
      toolResult,
      toolData,
      errorMessage,
    );

    return {
      type: "tool-call",
      toolCallId: toolId,
      toolName: toolName || "",
      args: parsedArgs,
      argsText: argsText || "",
      result,
      isError: !ToolUtils._isToolSuccessful(toolResult),
    };
  }

  /**
   * Processes a tool call and creates a tool call part with position information
   */
  static _processToolCall({
    toolId,
    toolCall,
  }: {
    toolId: string;
    toolCall: ToolCallData;
  }): ToolCallProcessingResult {
    const position = ToolUtils._extractPosition(toolCall);
    const componentData = ToolUtils._extractComponentData(toolCall);
    const toolData = ToolUtils._createToolData(
      toolCall.name,
      false,
      componentData,
    );

    const toolCallPart = ToolUtils._createToolCallPart({
      toolId,
      toolName: toolCall.front_component_name,
      argsText: toolCall.arguments,
      toolResult: toolCall.tool_result_status,
      toolData,
      errorMessage: toolCall.error || undefined,
    });

    return { toolCallPart, position };
  }

  /**
   * Organizes tool call parts by their position
   */
  static _organizeToolCallsByPosition(
    toolCalls: Record<string, ToolCallData> | null | undefined,
  ): {
    atStartTools: ThreadAssistantContentPart[];
    inConversationTools: ThreadAssistantContentPart[];
    atEndTools: ThreadAssistantContentPart[];
  } {
    const result = {
      atStartTools: [] as ThreadAssistantContentPart[],
      inConversationTools: [] as ThreadAssistantContentPart[],
      atEndTools: [] as ThreadAssistantContentPart[],
    };

    if (!toolCalls) {
      return result;
    }

    // Get the single entry as we expect only one element
    const [toolId, toolCall] = Object.entries(toolCalls)[0] || [];

    if (!toolId || !toolCall) {
      return result;
    }

    // Process the tool call directly
    const { toolCallPart, position } = ToolUtils._processToolCall({
      toolId,
      toolCall,
    });

    // Categorize by position
    if (ToolUtils._isToolAtStart(position)) {
      result.atStartTools.push(toolCallPart);
    } else if (ToolUtils._isToolInConversation(position)) {
      result.inConversationTools.push(toolCallPart);
    } else {
      result.atEndTools.push(toolCallPart);
    }

    return result;
  }

  /**
   * Processes tools from a tool message
   */
  static _processToolsFromMessage(
    toolMsg: ToolMessage,
    atStartTools: ThreadAssistantContentPart[],
    resultContentParts: ThreadAssistantContentPart[],
    atEndTools: ThreadAssistantContentPart[],
  ): void {
    if (!ToolUtils._hasValidToolCalls(toolMsg)) {
      return;
    }

    const {
      atStartTools: msgAtStartTools,
      inConversationTools,
      atEndTools: msgAtEndTools,
    } = ToolUtils._organizeToolCallsByPosition(toolMsg.tool_calls);

    // Handle start tools
    if (msgAtStartTools.length > 0) {
      atStartTools.push(...msgAtStartTools);
    }

    // Handle in-conversation tools
    if (inConversationTools.length > 0) {
      ToolUtils._markFirstToolInList(inConversationTools);
      resultContentParts.push(...inConversationTools);
    }

    // Handle end tools
    if (msgAtEndTools.length > 0) {
      atEndTools.push(...msgAtEndTools);
    }
  }

  /**
   * Adds a dot to the last text content part in the results array
   * @param result - Array of content parts to modify
   */
  static _addDotToLastTextContent(result: ThreadAssistantContentPart[]): void {
    // Find the last text content part from the end
    for (let i = result.length - 1; i >= 0; i--) {
      const part = result[i];
      if (part.type === "text") {
        // Replace the text content part with a new one that includes the dot (trim to remove trailing spaces)
        result[i] = ContentUtils.createTextContent(part.text.trimEnd() + ".");
        break;
      }
    }
  }

  /**
   * Processes text transformation tool calls from assistant messages
   * This handles tool calls that replace text at specific positions in the original content
   * @param content - The original text content
   * @param toolCalls - The tool calls to process
   */
  static _processTextTransformToolCalls(
    content: string,
    toolCalls: Record<string, ToolCallData> | null | undefined,
  ): ThreadAssistantContentPart[] {
    if (!toolCalls || Object.keys(toolCalls).length === 0) {
      return [ContentUtils.createTextContent(content)];
    }

    // Filter and sort text transformation tools by position
    const textTransformTools = Object.entries(toolCalls)
      .filter(
        ([_, toolCall]) =>
          toolCall.name === "pdf_source_display" &&
          toolCall.front_position === "in_conversation_tool_end",
      )
      .map(([toolId, toolCall]) => {
        const args = ToolUtils._parseToolArguments(toolCall.arguments);
        return {
          toolId,
          toolCall,
          position: args.position || 0,
          length: args.length || 0,
        };
      })
      .sort((a, b) => a.position - b.position); // Sort by position ascending

    if (textTransformTools.length === 0) {
      return [ContentUtils.createTextContent(content)];
    }

    const result: ThreadAssistantContentPart[] = [];
    let currentPosition = 0;

    // Process each text transformation tool
    textTransformTools.forEach(({ toolId, toolCall, position, length }) => {
      // Check if we need to consume a dot after this tool
      const shouldConsumeDot =
        content
          .slice(position + length)
          .trimStart()
          .charAt(0) === ".";
      const actualLength = shouldConsumeDot ? length + 1 : length;

      // Add text before the current tool (if any)
      if (position > currentPosition) {
        const beforeText = content.slice(currentPosition, position);
        result.push(ContentUtils.createTextContent(beforeText));
      }

      // Create the tool call part
      const componentData = ToolUtils._extractComponentData(toolCall);
      const toolData = ToolUtils._createToolData(
        toolCall.name,
        false, // Will be marked as first later if needed
        componentData,
      );

      const toolCallPart = ToolUtils._createToolCallPart({
        toolId,
        toolName: toolCall.front_component_name,
        argsText: toolCall.arguments,
        toolResult: toolCall.tool_result_status,
        toolData,
        errorMessage: toolCall.error || undefined,
      });

      result.push(toolCallPart);

      // If we need to consume a dot, add it to the last text content
      if (shouldConsumeDot) {
        ToolUtils._addDotToLastTextContent(result);
      }

      // Update current position to after the replaced text (including consumed dot)
      currentPosition = position + actualLength;
    });

    // Add remaining text after the last tool (if any)
    if (currentPosition < content.length) {
      const afterText = content.slice(currentPosition);
      if (afterText) {
        result.push(ContentUtils.createTextContent(afterText));
      }
    }

    // Mark the first tool as first if there are any tools
    const firstToolIndex = result.findIndex(
      (part) => part.type === "tool-call",
    );
    if (firstToolIndex !== -1) {
      const firstTool = result[firstToolIndex];
      if (firstTool.type === "tool-call" && "result" in firstTool) {
        const toolResult = firstTool.result as any;
        if (toolResult) {
          toolResult.isFirstTool = true;
        }
      }
    }

    return result;
  }
}

/**
 * Utility class for message tree operations
 */
export class MessageTreeUtils {
  /**
   * Builds a tree structure from an array of messages
   * @param messages - Array of messages to organize into a tree
   */
  static buildMessageTree(messages: ReadonlyArray<AIMessage>): MessageTree {
    const tree: MessageTree = {};

    // Initialize the tree with all messages
    messages.forEach((msg) => {
      const message = msg as { parent_id?: number | null; id: number };
      tree[message.id] = {
        msg,
        children: [],
        processed: false,
      };
    });

    // Establish parent-child relationships
    messages.forEach((msg) => {
      const message = msg as { parent_id?: number | null; id: number };
      const parentId = message.parent_id ?? null;

      if (parentId !== null && tree[parentId]) {
        tree[parentId].children.push(message.id);
      }
    });

    return tree;
  }

  /**
   * Finds consecutive assistant and tool messages in a tree
   * @param startId - ID of the starting message
   * @param tree - The message tree
   */
  static findConsecutiveGroupInTree(
    startId: number,
    tree: MessageTree,
  ): number[] {
    const result = [startId];
    let currentId = startId;

    // Continue adding child IDs as long as there's only one child and it's an assistant or tool message
    while (tree[currentId].children.length === 1) {
      const childId = tree[currentId].children[0];
      const childMsg = tree[childId].msg;

      if (MessageUtils.isAssistantOrToolMessage(childMsg)) {
        result.push(childId);
        currentId = childId;
      } else {
        break;
      }
    }

    return result;
  }

  /**
   * Finds root message IDs in a tree (messages with no parent or parent outside the tree)
   * @param messages - Array of all messages
   * @param tree - The message tree
   */
  static findRootMessageIds(
    messages: ReadonlyArray<AIMessage>,
    tree: MessageTree,
  ): number[] {
    return messages
      .filter((msg) => {
        const parentId = (msg as any).parent_id;
        return parentId === null || !tree[parentId];
      })
      .map((msg) => (msg as any).id);
  }
}
